/**
 * Pressbox-style editorial nav bar — "Stacked Editorial" layout.
 *
 * Desktop (md+):
 *   Row 1 — Nameplate: wordmark + tagline (left), date/vol + avatar (right)
 *   Row 2 — Nav strip: text links (left), Upload button (right); heavy bottom rule
 *
 * Mobile (below md):
 *   Single bar: logo + stacked text (left), avatar (right); heavy bottom rule
 *   Bottom navigation handled by MobileBottomBar in Layout.
 */
import { Link, useLocation } from 'react-router-dom';
import { SignInButton, SignedIn, SignedOut, UserButton, useUser } from '@clerk/clerk-react';
import { useEffect } from 'react';
import { useSyncAvatar, useUserProfile } from '@/hooks/useUserProfile';
import { FeatureGate } from '@/components/FeatureGate';
import { useFeatureFlag } from '@/contexts/FeatureFlagContext';
import { UploadIcon, TipJarIcon } from './NavIcons';
import { BellIcon } from '@/components/icons/BellIcon';
import { NotificationBell, PushPromptBanner } from '@/components/notifications';
import { IOSInstallBanner } from '@/components/pwa/IOSInstallBanner';

// Icons for UserButton menu items
const AdminIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const ScoringIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

/** Map child routes to their parent nav section so active states work on child pages. */
export function getActiveSection(pathname: string): string | null {
  // Check /scorecards before /score — /scorecards starts with /score
  if (pathname.startsWith('/scorecards/my')) return '/scorecards/my';
  if (pathname.startsWith('/scorecards') || pathname.startsWith('/boxscore')) return '/scorecards';
  if (pathname.startsWith('/scoring-101')) return '/scoring-101';
  if (pathname.startsWith('/score')) return '/score';
  if (pathname.startsWith('/gameday')) return '/gameday';
  if (pathname.startsWith('/tip-jar')) return '/tip-jar';
  return null;
}

interface PressboxNavProps {
  onUpload?: () => void;
}

export function PressboxNav({ onUpload }: PressboxNavProps) {
  const location = useLocation();
  const { user } = useUser();
  const { data: profile } = useUserProfile();
  const { mutate: syncAvatarMutate } = useSyncAvatar();

  const isAdmin = profile?.is_admin || profile?.is_super_admin;
  const isModerator = isAdmin || profile?.is_moderator;
  const showTipJar = useFeatureFlag('show_tip_jar');

  useEffect(() => {
    if (user?.imageUrl) {
      syncAvatarMutate(user.imageUrl);
    }
  }, [user?.imageUrl, syncAvatarMutate]);

  // Volume/issue: day of year
  const startOfYear = new Date(new Date().getFullYear(), 0, 0);
  const dayOfYear = Math.floor((Date.now() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));

  // Desktop date: "Sun, Feb 22, 2026"
  const desktopDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Mobile date: "Sun, Feb 22" (no year)
  const mobileDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  const activeSection = getActiveSection(location.pathname);

  // Shared nav link classes — used in the desktop nav strip
  const navLinkBase =
    'font-sans text-[11px] font-semibold uppercase tracking-[1px] py-[9px] px-[14px] border-b-[2.5px] -mb-[2px] transition-colors';
  const navLinkActive = 'text-ink font-bold border-ink';
  const navLinkInactive = 'text-warm-gray border-transparent hover:text-ink';

  const navLinkClass = (path: string) =>
    `${navLinkBase} ${activeSection === path ? navLinkActive : navLinkInactive}`;

  const ariaCurrent = (path: string) => (activeSection === path ? ('page' as const) : undefined);

  // Shared UserButton menu items — rendered in both desktop and mobile instances
  const userMenuItems = (
    <UserButton.MenuItems>
      <UserButton.Link label="Notifications" labelIcon={<BellIcon />} href="/notifications" />
      <UserButton.Link
        label="Scoring Defaults"
        labelIcon={<ScoringIcon />}
        href="/profile/scoring-defaults"
      />
      {showTipJar && (
        <UserButton.Link label="Support" labelIcon={<TipJarIcon size={16} />} href="/tip-jar" />
      )}
      {isAdmin && <UserButton.Link label="Admin" labelIcon={<AdminIcon />} href="/admin" />}
    </UserButton.MenuItems>
  );

  return (
    <div className="sticky top-0 z-50">
      {/* ------------------------------------------------------------------ */}
      {/* DESKTOP (md+)                                                        */}
      {/* ------------------------------------------------------------------ */}

      {/* Row 1 — Nameplate */}
      <div className="hidden md:block bg-white">
        <div className="max-w-7xl mx-auto py-2 px-6 flex items-center justify-between">
          {/* Left: Diamond logo + wordmark stack */}
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-diamond-light.svg" alt="" className="w-auto max-h-8" />
            <div className="flex flex-col">
              <span
                className="font-display text-[22px] font-black text-ink leading-none"
                style={{ letterSpacing: '-0.5px' }}
              >
                THE LINESCORE
              </span>
              <span
                className="font-serif text-[11px] text-warm-gray uppercase"
                style={{ letterSpacing: '2px' }}
              >
                The Slow Game in Longhand
              </span>
            </div>
          </Link>

          {/* Right: Date/vol + avatar */}
          <div className="flex items-center gap-4">
            <span
              className="font-serif text-[11px] text-warm-gray uppercase"
              style={{ letterSpacing: '1px' }}
            >
              {desktopDate} &middot; Vol. I &middot; No. {dayOfYear}
            </span>

            <SignedOut>
              <SignInButton mode="modal">
                <button
                  className="font-sans text-[13px] text-espresso uppercase hover:text-ink transition-colors cursor-pointer"
                  style={{ letterSpacing: '1.5px' }}
                >
                  Sign In
                </button>
              </SignInButton>
            </SignedOut>

            <SignedIn>
              <NotificationBell />
              <UserButton
                key={`ub-desktop-${isAdmin}-${isModerator}`}
                afterSignOutUrl="/"
                userProfileMode="navigation"
                userProfileUrl="/profile"
                appearance={{
                  elements: {
                    avatarBox: 'h-8 w-8 ring-[1.5px] ring-ink',
                    userButtonTrigger:
                      'focus:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
                  },
                }}
              >
                {userMenuItems}
              </UserButton>
            </SignedIn>
          </div>
        </div>
      </div>

      {/* Row 2 — Nav strip (heavy bottom rule) */}
      <nav aria-label="Main navigation" className="hidden md:block bg-white border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Left: text nav links */}
          <div className="flex items-center">
            <FeatureGate flag="show_score_page">
              <Link
                to="/score"
                className={navLinkClass('/score')}
                aria-current={ariaCurrent('/score')}
              >
                Score
              </Link>
            </FeatureGate>

            <Link
              to="/scorecards"
              className={navLinkClass('/scorecards')}
              aria-current={ariaCurrent('/scorecards')}
            >
              Archive
            </Link>

            <Link
              to="/scorecards/my"
              className={navLinkClass('/scorecards/my')}
              aria-current={ariaCurrent('/scorecards/my')}
            >
              My Scorebook
            </Link>

            <Link
              to="/gameday"
              className={navLinkClass('/gameday')}
              aria-current={ariaCurrent('/gameday')}
            >
              Games
            </Link>

            <FeatureGate flag="show_tip_jar">
              <Link
                to="/tip-jar"
                className={navLinkClass('/tip-jar')}
                aria-current={ariaCurrent('/tip-jar')}
              >
                Support
              </Link>
            </FeatureGate>

            <FeatureGate flag="show_scoring_101">
              <Link
                to="/scoring-101"
                className={navLinkClass('/scoring-101')}
                aria-current={ariaCurrent('/scoring-101')}
                data-testid="nav-scoring-101"
              >
                Scoring 101
              </Link>
            </FeatureGate>
          </div>

          {/* Right: Upload button */}
          <button
            onClick={onUpload}
            className="flex items-center gap-1.5 bg-ink text-linen font-sans text-[11px] font-bold uppercase tracking-[1.2px] py-[7px] px-[14px] rounded-[3px] cursor-pointer hover:bg-ink/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink"
          >
            <UploadIcon size={13} color="currentColor" />
            Upload
          </button>
        </div>
      </nav>

      {/* ------------------------------------------------------------------ */}
      {/* MOBILE (below md)                                                    */}
      {/* ------------------------------------------------------------------ */}

      <div className="md:hidden bg-white border-b-2 border-ink">
        <div className="py-2 px-4 flex items-center justify-between">
          {/* Left: logo + stacked text */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-diamond-light.svg" alt="" className="w-auto max-h-7" />
            <div className="flex flex-col">
              <span className="font-display text-[16px] font-black text-ink leading-none">
                THE LINESCORE
              </span>
              <span
                className="font-serif text-[9px] text-warm-gray-light uppercase"
                style={{ letterSpacing: '1px' }}
              >
                {mobileDate} &middot; Vol. I No. {dayOfYear}
              </span>
            </div>
          </Link>

          {/* Right: avatar */}
          <SignedOut>
            <SignInButton mode="modal">
              <button
                className="font-sans text-[13px] text-espresso uppercase hover:text-ink transition-colors cursor-pointer"
                style={{ letterSpacing: '1.5px' }}
              >
                Sign In
              </button>
            </SignInButton>
          </SignedOut>

          <SignedIn>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <UserButton
                key={`ub-mobile-${isAdmin}-${isModerator}`}
                afterSignOutUrl="/"
                userProfileMode="navigation"
                userProfileUrl="/profile"
                appearance={{
                  elements: {
                    avatarBox: 'h-[30px] w-[30px] ring-[1.5px] ring-ink',
                    userButtonTrigger:
                      'focus:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
                  },
                }}
              >
                {userMenuItems}
              </UserButton>
            </div>
          </SignedIn>
        </div>
      </div>

      {/* Push notification prompt banners (signed-in users only) */}
      <SignedIn>
        <FeatureGate flag="enable_push_notifications">
          <PushPromptBanner />
          <IOSInstallBanner />
        </FeatureGate>
      </SignedIn>
    </div>
  );
}
